'use client';

import { useState } from 'react';

import { ChevronDown, ChevronUp, Info, Instagram, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Card, CardContent } from '@kit/ui/card';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kit/ui/sheet';
import { cn } from '@kit/ui/utils';

import { useBookingPresentation } from './booking-presentation';
import styles from './native-booking-controls.module.css';

interface TrainerCardProps {
  trainer: {
    id: string;
    name: string;
    bio?: string | null;
    picture_url?: string | null;
    instagram_handle?: string | null;
    phone_number?: string | null;
    normal_price?: number | null;
    peak_price?: number | null;
    applicable_price?: number | null; // Server-calculated price
    per_person_price?: number | null;
    max_participants?: number;
  };
  isSelected: boolean;
  onSelect: () => void;
  isPeak?: boolean; // For display purposes only (showing peak badge)
}

export function TrainerCard({
  trainer,
  isSelected,
  onSelect,
}: TrainerCardProps) {
  const presentation = useBookingPresentation();
  const { t } = useTranslation();
  const [bioExpanded, setBioExpanded] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Use server-calculated applicable_price if available, otherwise fallback to normal_price
  const price = trainer.applicable_price ?? trainer.normal_price;
  const hasContactInfo = trainer.phone_number || trainer.instagram_handle;

  return (
    <Card
      data-selected={isSelected}
      className={cn(
        styles.optionCard,
        'relative cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-primary ring-2',
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 shrink-0">
            <AvatarImage src={trainer.picture_url || undefined} />
            <AvatarFallback>{getInitials(trainer.name)}</AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-lg font-semibold">{trainer.name}</h3>

              {hasContactInfo && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      className="hover:bg-muted shrink-0 touch-manipulation rounded-md p-2 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info className="text-muted-foreground h-5 w-5" />
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    data-native-booking-bottom-sheet
                    side="bottom"
                    className={cn('max-h-[50vh]', presentation.className)}
                    style={presentation.style}
                  >
                    <SheetHeader>
                      <SheetTitle>{trainer.name}</SheetTitle>
                      <SheetDescription>
                        {t('bookings:contactInformation')}
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                      {trainer.phone_number && (
                        <div className="flex items-center gap-3">
                          <Phone className="text-muted-foreground h-5 w-5" />
                          <a
                            href={`tel:${trainer.phone_number}`}
                            className="text-base hover:underline"
                          >
                            {trainer.phone_number}
                          </a>
                        </div>
                      )}
                      {trainer.instagram_handle && (
                        <div className="flex items-center gap-3">
                          <Instagram className="text-muted-foreground h-5 w-5" />
                          <a
                            href={`https://instagram.com/${trainer.instagram_handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base hover:underline"
                          >
                            @{trainer.instagram_handle}
                          </a>
                        </div>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
            </div>

            {trainer.bio && (
              <div className="mt-1">
                <p
                  className={cn(
                    'text-muted-foreground text-sm',
                    !bioExpanded && 'line-clamp-2',
                  )}
                >
                  {trainer.bio}
                </p>
                {trainer.bio.length > 100 && (
                  <button
                    type="button"
                    className="text-primary mt-1 flex touch-manipulation items-center gap-1 text-xs hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBioExpanded(!bioExpanded);
                    }}
                  >
                    {bioExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" />
                        {t('common:showLess')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" />
                        {t('common:readMore')}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            <div className="mt-3 flex items-center justify-between">
              {price !== null && price !== undefined && (
                <div className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1 text-lg font-semibold">
                    <CurrencyIcon className="h-4 w-4" /> {price.toFixed(2)}
                  </span>
                  {trainer.per_person_price != null &&
                    trainer.per_person_price > 0 && (
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        +<CurrencyIcon className="h-3 w-3" />{' '}
                        {trainer.per_person_price.toFixed(2)}
                        {t('bookings:perPerson')}
                      </span>
                    )}
                </div>
              )}

              {isSelected && (
                <Badge variant="default" className="ms-auto">
                  {t('bookings:selected')}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
